import { useState } from 'react';
import DOMPurify from 'dompurify';
import { ArrowDownLeft, ArrowUpRight, ChevronDown, ChevronUp, Reply, Sparkles, Loader2, CornerDownLeft, CheckSquare, Square } from 'lucide-react';
import { api, fmtDate } from './api';

const SENTIMENT_BADGE = {
  positiv:    { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Positiv' },
  negativ:    { bg: 'bg-red-50',     text: 'text-red-700',     label: 'Negativ' },
  rueckfrage: { bg: 'bg-amber-50',   text: 'text-amber-700',   label: 'Rückfrage' },
  neutral:    { bg: 'bg-gray-50',    text: 'text-gray-700',    label: 'Neutral' },
};

const PURIFY = {
  ALLOWED_TAGS: ['p','br','div','span','strong','em','b','i','u','s','sub','sup','a','ul','ol','li','h1','h2','h3','h4','h5','h6','blockquote','code','pre','table','thead','tbody','tr','th','td','hr','img'],
  ALLOWED_ATTR: ['href','target','rel','src','alt','width','height','class','title','style','cite','type','data-cid'],
};

function dispatchMailChanged() {
  try { window.dispatchEvent(new CustomEvent('weship:mail-changed')); } catch { /* noop */ }
}

export default function MailItem({ mail, onMarkedRead, onChanged, onReply }) {
  const [open, setOpen] = useState(false);
  const [showFull, setShowFull] = useState(false);
  const [marking, setMarking] = useState(false);
  const [busy, setBusy] = useState(false);
  const isInbound = mail.direction === 'in';
  const isUnread = isInbound && !mail.is_read && !mail.is_done;
  const needsReply = !!mail.needs_reply && !mail.is_done;
  const isDone = !!mail.is_done;
  const senStyle = SENTIMENT_BADGE[mail.sentiment] || null;

  // Endpoint-Namespace je nach Quelle
  const isReplySource = mail.source_type === 'inbound_reply';
  const flagBase = isReplySource
    ? `/api/sales/replies/${mail.reply_id || mail.id}`
    : `/api/sales/aktivitaeten/${mail.id}`;

  const handleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next && isUnread && !marking) {
      setMarking(true);
      try {
        if (isReplySource) {
          await api(`/api/sales/replies/${mail.reply_id || mail.id}/mark-read`, { method: 'POST' });
        }
        // Manual-Inbound: Auto-Mark-Read passiert serverseitig beim GET /api/leads/:id
        // Hier nur lokales State-Update + Sidebar-Event
        onMarkedRead?.(mail.reply_id || mail.id);
        dispatchMailChanged();
      } catch (e) {
        // silent
      } finally {
        setMarking(false);
      }
    }
  };

  const toggleFlag = async (e, kind) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const ep = kind === 'needs-reply' ? `${flagBase}/toggle-needs-reply` : `${flagBase}/toggle-is-done`;
      await api(ep, { method: 'POST' });
      onChanged?.();
      dispatchMailChanged();
    } catch (err) {
      // silent
    } finally {
      setBusy(false);
    }
  };

  const html = showFull && mail.body_full ? mail.body_full : mail.body_html || '';
  const Icon = isInbound ? ArrowDownLeft : ArrowUpRight;
  const sender = isInbound ? (mail.party_name || mail.party_email || 'Lead') : (mail.party_name || 'WeShip');
  const recipient = isInbound ? 'WeShip' : (mail.party_email || mail.recipient || '—');

  // Visuelle Klassen je nach State
  const containerClass = isDone
    ? 'border-[var(--border-card)] bg-[var(--bg-inset)] opacity-70'
    : isUnread
      ? 'border-[#338edc] shadow-sm border-l-4 border-l-[#338edc]'
      : needsReply
        ? 'border-amber-300 bg-amber-50/30'
        : 'border-[var(--border-card)]';

  const subjectClass = isDone
    ? 'text-[var(--text-tertiary)] line-through'
    : isUnread
      ? 'text-[var(--text-primary)] font-bold'
      : 'text-[var(--text-primary)] font-medium';

  return (
    <div className={`border rounded-lg bg-[var(--bg-card)] transition-colors ${containerClass}`}>
      <button
        type="button"
        onClick={handleOpen}
        className="w-full flex items-start gap-3 px-3 py-2.5 hover:bg-[var(--bg-inset)] text-left"
      >
        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${isInbound ? 'bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200' : 'bg-[#EBF4FD] text-[#338edc] ring-1 ring-[#338edc]/20'}`}>
          <Icon className="w-5 h-5" strokeWidth={2.25} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-[11px] text-[var(--text-tertiary)] mb-0.5 flex-wrap">
            <span className="font-medium text-[var(--text-secondary)]">{sender}</span>
            <span>→</span>
            <span>{recipient}</span>
            {mail.workflow_name && (
              <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-[var(--bg-inset)] text-[var(--text-secondary)] border border-[var(--border-card)]">
                Workflow: {mail.workflow_name}
              </span>
            )}
            {needsReply && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-300">
                <CornerDownLeft className="w-2.5 h-2.5" /> zu beantworten
              </span>
            )}
            {isDone && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                <CheckSquare className="w-2.5 h-2.5" /> erledigt
              </span>
            )}
            <span className="ml-auto">{fmtDate(mail.at)}</span>
            {isUnread && <span className="w-2 h-2 rounded-full bg-[#338edc]" title="Ungelesen" />}
          </div>
          <div className={`text-[13px] truncate ${subjectClass}`}>{mail.subject}</div>
          {!open && mail.preview && (
            <div className={`text-[12px] truncate mt-0.5 ${isDone ? 'text-[var(--text-muted,#9CA3AF)] line-through' : 'text-[var(--text-tertiary)]'}`}>{mail.preview}</div>
          )}
          {senStyle && open && (
            <div className="flex items-center gap-2 mt-2">
              <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${senStyle.bg} ${senStyle.text}`}>
                {senStyle.label}
              </span>
              {mail.summary && (
                <span className="text-[11px] text-[var(--text-secondary)] italic flex items-center gap-1">
                  <Sparkles className="w-3 h-3 text-[#338edc]" />
                  {mail.summary}
                </span>
              )}
            </div>
          )}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-[var(--text-tertiary)] flex-shrink-0 mt-1" /> : <ChevronDown className="w-4 h-4 text-[var(--text-tertiary)] flex-shrink-0 mt-1" />}
      </button>

      {open && (
        <div className="border-t border-[var(--border-card)] px-3 py-3">
          <div
            className="prose prose-sm max-w-none text-[13px] text-[var(--text-primary)] [&_a]:text-[#338edc] [&_a]:underline [&_img]:max-w-full [&_table]:border [&_th]:border [&_td]:border [&_blockquote]:border-l-4 [&_blockquote]:border-[var(--border-card)] [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:text-[var(--text-tertiary)]"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html, PURIFY) }}
          />
          <div className="flex items-center gap-2 mt-3 pt-2 border-t border-[var(--border-card)]/50 flex-wrap">
            {mail.has_more && (
              <button
                onClick={(e) => { e.stopPropagation(); setShowFull((v) => !v); }}
                className="text-[11px] text-[#338edc] hover:underline"
              >
                {showFull ? 'Kompakt anzeigen' : 'Vollständige E-Mail anzeigen'}
              </button>
            )}
            {marking && <Loader2 className="w-3 h-3 animate-spin text-[var(--text-tertiary)]" />}

            {/* #1054 — Manual-Flag-Toggles (auf jeder Mail außer Workflow-Outbound) */}
            {mail.source_type !== 'workflow_outbound' && (
              <div className="flex items-center gap-1.5 ml-auto">
                <button
                  onClick={(e) => toggleFlag(e, 'needs-reply')}
                  disabled={busy}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium border transition-colors ${
                    needsReply
                      ? 'bg-amber-100 text-amber-800 border-amber-300'
                      : 'bg-[var(--bg-card)] text-[var(--text-secondary)] border-[var(--border-card)] hover:bg-[var(--bg-inset)]'
                  } disabled:opacity-50`}
                  title={needsReply ? 'Markierung „zu beantworten" entfernen' : 'Als „zu beantworten" markieren'}
                >
                  <CornerDownLeft className="w-3 h-3" />
                  {needsReply ? 'zu beantworten' : 'zu beantworten?'}
                </button>
                <button
                  onClick={(e) => toggleFlag(e, 'is-done')}
                  disabled={busy}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium border transition-colors ${
                    isDone
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : 'bg-[var(--bg-card)] text-[var(--text-secondary)] border-[var(--border-card)] hover:bg-[var(--bg-inset)]'
                  } disabled:opacity-50`}
                  title={isDone ? 'Erledigt-Markierung entfernen' : 'Als erledigt markieren'}
                >
                  {isDone ? <CheckSquare className="w-3 h-3" /> : <Square className="w-3 h-3" />}
                  {isDone ? 'erledigt' : 'erledigt?'}
                </button>
              </div>
            )}

            {isInbound && onReply && !isDone && (
              <button
                onClick={(e) => { e.stopPropagation(); onReply(mail); }}
                className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[#338edc] text-white text-[11px] hover:opacity-90"
              >
                <Reply className="w-3 h-3" /> Antworten
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
